<template>
  <div class="cascader-panel-box">
    <div class="cascader-panel">
      <div class="panel">
        <div class="box-title search-box" v-if="filterable">
          <el-select
            style="width: 300px"
            v-model="searchValue"
            filterable
            popper-append-to-body
            popper-class="cascader-filter-popper"
            size="mini"
            :filter-method="filterFunc"
            @change="onSearchSelectChange"
            placeholder="输入搜索内容"
          >
            <el-option v-for="item in filteredItems" :key="item.value" :label="item.label" :value="item.value"> </el-option>
          </el-select>
        </div>
        <el-cascader-panel
          ref="cascaderRef"
          v-model="cascaderModel"
          v-bind="$attrs"
          :options="options"
          @change="onCascaderChange"
        ></el-cascader-panel>
      </div>
      <div class="results">
        <div class="box-title result-box">
          <div>
            <span>已选结果</span>
            <span>（{{ results.length }}</span>
            <span v-if="max">/{{ max }}</span>
            <span>）</span>
          </div>

          <span class="clear" @click="clearAll">清空</span>
        </div>

        <div class="list">
          <div class="item-row value-name" v-for="(item, idx) in results" :key="idx">
            <span class="name">{{ item.label }}</span>
            <i class="close el-icon-error" @click="removeItem(item)"></i>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick, toRefs, watch } from 'vue'
// eslint-disable-next-line
const props = defineProps({ value: Array, max: Number, options: Array, filterable: { type: Boolean, default: false } })
// eslint-disable-next-line
const emits = defineEmits(['input'])
const { options } = toRefs(props)

const searchValue = ref('')
const cascaderRef = ref(null)
const cascaderModel = ref([])
const results = ref([])

const filteredItems = ref([])

watch(() => props.value, initProps, { immediate: true })

async function initProps() {
  if (props.value?.length) {
    await nextTick()
    // 根据v-model回显
    cascaderModel.value = getNodeFullPath(props.value)
    await nextTick()
    // 更新已选结果
    getCheckedNodes()
  }
}

/**
 * @description: 根据v-model的值，获取节点的完整路径，用于el-cascader-panel显示
 * @param {*} arr
 * @return [[50,5000,500001]]
 */
function getNodeFullPath(arr) {
  const values = []
  // 递归获取每个层级的label 和 value
  const loop = (data, items = []) => {
    data.forEach((node) => {
      node.items = items.concat(node)
      // 枝干节点和叶子节点包含都算
      const label = node.items.map((i) => i.id)
      if (node.children) {
        loop(node.children, node.items)
      }
      if (arr.some((i) => label.includes(i)) && !node.children) {
        values.push(node.items)
      }
    })
  }
  loop(options.value)
  return values.map((i) => i.map((j) => j.id))
}

/**
 * @description: 根据筛选值，获取包含筛选值的节点的完整路径
 * @param {*} val
 * @return {*}
 */
function getNodeLabelValueLoop(val) {
  const filteredValues = []
  // 递归获取每个层级的label 和 value
  const loop = (data, items = []) => {
    data.forEach((node) => {
      node.items = items.concat(node)
      const label = node.items.map((i) => i.value.toLowerCase()).join(',')
      val = val.toLowerCase()
      if (node.children) {
        loop(node.children, node.items)
      }
      if (label.includes(val) && !node.children) {
        filteredValues.push(node.items)
      }
    })
  }

  loop(options.value)

  return filteredValues
}

function filterFunc(val) {
  if (!val) {
    filteredItems.value = []
    return
  }
  const filteredValues = getNodeLabelValueLoop(val)
  // 获取由label和value 组成的筛选后的结果用于下拉列表
  const items = filteredValues.map((paths) => {
    return {
      label: paths.map((p) => p.value).join('/'),
      value: paths.map((p) => p.id).join(',')
    }
  })
  filteredItems.value = items
}

function getCheckedNodes() {
  const nodes = cascaderRef.value.getCheckedNodes()

  // 如果子节点全部被勾选，显示父节点，否则显示子节点
  const parentNodes = []
  const loop = (nodes) => {
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i]

      if (node.checked) {
        // 全部勾选，显示父节点
        const isExist = parentNodes.some((pNode) => node.path.includes(pNode.value))
        if (!isExist) {
          parentNodes.push(node)
        }
        continue
      } else if (!node.checked && node.indeterminate && node.hasChildren) {
        // 部分勾选，显示其勾选的子节点
        loop(node.children)
      } else if (!node.checked && !node.indeterminate) {
        continue
      }
    }
  }

  loop(nodes)
  results.value = parentNodes
}

function onCascaderChange(nodes) {
  cascaderModel.value = nodes.slice(0, props.max)
  nextTick(() => {
    getCheckedNodes()
    triggerUpdate()
  })
}

function onSearchSelectChange() {
  // 把已选值转换成字符串用于排重
  const cascaderModelStr = cascaderModel.value.map((v) => v.join(','))
  // 排除重复选择
  const valueArr = new Set([...cascaderModelStr, searchValue.value])
  const newValue = Array.from(valueArr).map((v) => v.split(','))
  cascaderModel.value = newValue.slice(0, props.max)
  searchValue.value = ''
  filteredItems.value = []

  nextTick(() => {
    getCheckedNodes()
    triggerUpdate()
  })
}

function removeItem(node) {
  const path = node.path.join(',')
  cascaderModel.value = cascaderModel.value.filter((n) => n.join(',').startsWith(path) === false)
  nextTick(() => {
    getCheckedNodes()
    triggerUpdate()
  })
}

function triggerUpdate() {
  const ids = results.value.map((n) => n.value)
  emits('input', ids)
}

function clearAll() {
  cascaderModel.value = []
  results.value = []
  emits('input', [])
}
</script>

<style>
.cascader-filter-popper {
  z-index: 9999 !important;
}
</style>

<style lang="scss" scoped>
@import '~common/styles/element-ui/variables.scss';
.search-box {
  margin-bottom: 4px;
}
::v-deep .el-cascader-panel {
  font-size: 12px;
  &.is-bordered {
    border: none;
  }
}

.cascader-panel {
  display: flex;
  border: solid 1px #dfe4ed;
  border-radius: 4px;

  ::v-deep .el-cascader-menu__wrap {
    height: 204px !important;
  }

  .box-title {
    background: #fafbfe;
    padding: 0 10px;
    border-bottom: 1px solid #dee4f5;
  }

  .panel {
    flex: 1;
  }

  .results {
    border-left: 1px solid #dee4f5;
    min-width: 260px;
    font-size: 12px;
    max-height: 230px;
    overflow: auto;

    .result-box {
      display: flex;
      justify-content: space-between;
      .clear {
        cursor: pointer;
        color: $primary-color;
      }
    }

    .list {
      height: calc(100% - 33px);
      overflow: auto;
      .item-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0 10px;

        &:hover {
          background: #eee;
          .close {
            display: inline-block;
          }
        }
        .close {
          display: none;
          cursor: pointer;
        }
      }
    }
  }
}
</style>
